import React, { useCallback, useRef } from "react";
import DashboardContent from "../components/DashboardContent";
import Footer from "../components/Footer";
import TriloChatComposer from "../components/TriloChatComposer";
import TriloChatHeader from "../components/TriloChatHeader";
import TriloChatMessageList from "../components/TriloChatMessageList";
import TriloChatSessionsSidebar from "../components/TriloChatSessionsSidebar";
import TriloChatWelcome from "../components/TriloChatWelcome";
import { useTriloChatDrafts } from "../hooks/useTriloChatDrafts";
import { useTriloChatExport } from "../hooks/useTriloChatExport";
import { useTriloChatSessions } from "../hooks/useTriloChatSessions";
import { usePermissions } from "../contexts/PermissionsContext";
import { PERMISSIONS } from "../utils/permissions";
import "../scss/chat.scss";

/**
 * MW (TriloChat) — the conversational view over a study's CSR data.
 *
 * This page only wires things together: conversation state lives in
 * hooks/useTriloChatSessions.js, draft decisions in useTriloChatDrafts.js, the
 * PDF export in useTriloChatExport.js, and every piece of the UI is a
 * components/TriloChat*.js component styled by scss/chat.scss.
 *
 * The MW RBAC permissions from /me/permissions are resolved here.
 */
export default function TriloChat() {
  const { hasPermission } = usePermissions();

  const canReadSessions = hasPermission(PERMISSIONS.mwSessionsRead);
  const canCreateSessions = hasPermission(PERMISSIONS.mwSessionsCreate);
  const canUpdateSessions = hasPermission(PERMISSIONS.mwSessionsUpdate);
  const canDeleteSessions = hasPermission(PERMISSIONS.mwSessionsDelete);
  const canDownload = hasPermission(PERMISSIONS.mwDownloadsRead);
  // Posting a message is both an update to the session and a new message, and
  // the backend checks both, so the composer needs the pair.
  const canSendMessage =
    canUpdateSessions && hasPermission(PERMISSIONS.mwMessagesCreate);

  const textareaRef = useRef(null);
  const focusComposer = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  const {
    sessions,
    activeSessionId,
    activeSession,
    messages,
    isStreaming,
    activeToolCalls,
    loadingSessions,
    studies,
    selectedStudyId,
    setSelectedStudyId,
    quickReplies,
    selectSession,
    createNewSession,
    deleteSession,
    handleStudyChange,
    sendMessage,
  } = useTriloChatSessions({
    onNewSession: focusComposer,
    canRead: canReadSessions,
    canCreate: canCreateSessions,
    canUpdate: canUpdateSessions,
    canSendMessage,
    canDelete: canDeleteSessions,
  });

  const { draftStates, setDraftStates } = useTriloChatDrafts(activeSessionId);

  const { isExporting, exportConversation } = useTriloChatExport({
    sessionId: activeSessionId,
    session: activeSession,
    messages,
    draftStates,
  });

  const pageContent = (
    <div className="trilochat">
      <TriloChatSessionsSidebar
        sessions={sessions}
        activeSessionId={activeSessionId}
        loadingSessions={loadingSessions}
        onSelectSession={selectSession}
        onDeleteSession={deleteSession}
        onNewChat={() => createNewSession(null)}
        canCreate={canCreateSessions}
        canDelete={canDeleteSessions}
      />

      <div className="trilochat__main">
        {!activeSessionId ? (
          <TriloChatWelcome
            studies={studies}
            selectedStudyId={selectedStudyId}
            onStudyChange={setSelectedStudyId}
            onStart={() => createNewSession(selectedStudyId)}
            disabled={!canCreateSessions}
          />
        ) : (
          <>
            <TriloChatHeader
              title={activeSession?.title}
              canExport={messages.length > 0}
              isExporting={isExporting}
              onExport={exportConversation}
              studies={studies}
              selectedStudyId={selectedStudyId}
              onStudyChange={handleStudyChange}
              canDownload={canDownload}
            />

            <TriloChatMessageList
              messages={messages}
              sessionId={activeSessionId}
              isStreaming={isStreaming}
              activeToolCalls={activeToolCalls}
              draftStates={draftStates}
              setDraftStates={setDraftStates}
            />

            <TriloChatComposer
              quickReplies={quickReplies}
              onSend={sendMessage}
              isStreaming={isStreaming}
              disabled={!canSendMessage}
              textareaRef={textareaRef}
            />
          </>
        )}

        <Footer />
      </div>
    </div>
  );

  return (
    <DashboardContent
      sidebarCSR={false}
      headerTitle=""
      headerButton={false}
      pageContent={pageContent}
      hideFooter
    />
  );
}
